import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle } from 'lucide-react';
import { FormEvent, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ManualCardPaymentForm, type ManualCardDetails } from '@/components/payments/ManualCardPaymentForm.js';
import { PrintButton } from '@/components/printing/PrintButton.js';
import { UpsellSuggestionsPanel } from '@/components/upsell/UpsellSuggestionsPanel.js';
import { CheckoutAddItemPanel } from '@/components/checkout/CheckoutAddItemPanel.js';
import { WaiverModal } from '@/components/tickets/WaiverModal.js';
import { checkout, listPaymentsByTicket } from '@/lib/api/payments.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import {
  addLineItem,
  getTicket,
  signTicketWaiver,
  type WaiverCollectionMode,
} from '@/lib/api/tickets.js';
import type { UpsellSuggestionResult } from '@/lib/api/customer-status-upsell.js';
import { amountDueForMethod, calculateCashDiscount, terminalEntryAmount } from '@/lib/cash-discount.js';
import { resolveTicketBalance } from '@/lib/ticket-balance.js';
import { linePreTaxTotal } from '@/lib/money/line-totals.js';
import {
  isPaymentVoided,
  paymentMethodLabel,
  toPaymentRecord,
} from '@/lib/payment-display.js';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { usePinVerification } from '@/hooks/usePinVerification.js';
import { PinDialog } from '@/components/common/PinDialog.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { useAuthStore } from '@/stores/auth.js';
import { roleMeetsMinimum } from '@/lib/permissions.js';
import type { PaymentMethod } from '@fixflow/shared-types';

const METHODS: PaymentMethod[] = ['cash', 'card', 'store_credit', 'other'];

type Line = { method: PaymentMethod; amount: string };

export function CheckoutPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const pinVerification = usePinVerification();
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();
  const userRole = useAuthStore((s) => s.user?.role ?? 'read_only');
  const [payments, setPayments] = useState<Line[]>([{ method: 'cash', amount: '' }]);
  const [cashTendered, setCashTendered] = useState('');
  const [cardDetails, setCardDetails] = useState<ManualCardDetails>({
    amount: 0,
    transactionId: '',
    cardType: '',
    lastFour: '',
  });
  const [success, setSuccess] = useState(false);
  const [partialSuccess, setPartialSuccess] = useState(false);
  const [checkoutWaiverOpen, setCheckoutWaiverOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [upsellPrefill, setUpsellPrefill] = useState<string | undefined>(undefined);
  const [tipAmount, setTipAmount] = useState('');
  const [tipPin, setTipPin] = useState('');
  const [paidAtLocal, setPaidAtLocal] = useState(() => {
    const d = new Date();
    const offsetMs = d.getTimezoneOffset() * 60 * 1000;
    return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
  });
  const [acknowledgeClosedSession, setAcknowledgeClosedSession] = useState(false);
  const [closedSessionWarning, setClosedSessionWarning] = useState<string | null>(null);
  const [managerOverrideAuthorizedBy, setManagerOverrideAuthorizedBy] = useState<string | undefined>();

  const ticketQuery = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => getTicket(id!),
    enabled: Boolean(id),
  });
  const storeQuery = useQuery({
    queryKey: ['store-settings'],
    queryFn: getCurrentStore,
  });
  const paymentsQuery = useQuery({
    queryKey: ['payments', 'ticket', id],
    queryFn: () => listPaymentsByTicket(id!),
    enabled: Boolean(id),
  });

  const ticket = ticketQuery.data?.ticket;
  const balance = useMemo(
    () =>
      resolveTicketBalance({
        total: ticket?.total,
        paidTotal: ticket?.paidTotal,
        balanceDue: ticket?.balanceDue,
      }),
    [ticket?.total, ticket?.paidTotal, ticket?.balanceDue],
  );

  const activePayments = useMemo(() => {
    const rows = (paymentsQuery.data ?? []).map((p) => toPaymentRecord(p));
    return rows.filter((p) => !isPaymentVoided(p) && Number(p.amount) !== 0);
  }, [paymentsQuery.data]);

  const hasReceiptPrinter = Boolean(storeQuery.data?.printnodeReceiptPrinterId);
  const primaryDeviceId = ticketQuery.data?.devices?.[0]?.id ?? null;

  // Cash discount applies to BALANCE DUE (amount still to collect), not order total.
  const cashDiscount = useMemo(
    () =>
      calculateCashDiscount(balance.amountToCollect, {
        cashDiscountEnabled: storeQuery.data?.cashDiscountEnabled,
        cashDiscountRate: storeQuery.data?.cashDiscountRate,
        cashDiscountMode: storeQuery.data?.cashDiscountMode,
        cashDiscountLabel: storeQuery.data?.cashDiscountLabel,
      }),
    [balance.amountToCollect, storeQuery.data],
  );

  const primaryMethod = payments[0]?.method ?? 'cash';
  const methodDue = amountDueForMethod(cashDiscount, primaryMethod);
  const tipNum = Math.max(0, Number(tipAmount) || 0);
  const tipsEnabled = Boolean(storeQuery.data?.tipsEnabled);
  const tipsAllowedForMethod =
    tipsEnabled &&
    ((primaryMethod === 'card' && storeQuery.data?.tipsOnCard !== false) ||
      (primaryMethod === 'cash' && storeQuery.data?.tipsOnCash === true));
  const terminalAddsSurcharge =
    (storeQuery.data?.terminalAppliesSurcharge ?? true) &&
    primaryMethod === 'card' &&
    cashDiscount.mode === 'card_surcharge' &&
    cashDiscount.discountAmount > 0;
  const readerAmount = terminalAddsSurcharge ? cashDiscount.cashTotal : methodDue;
  const chargedAmount = primaryMethod === 'card' ? cashDiscount.cardTotal : methodDue;
  const totalCharged = Math.round((chargedAmount + (tipsAllowedForMethod ? tipNum : 0)) * 100) / 100;
  const terminalEntry = useMemo(
    () =>
      terminalEntryAmount(cashDiscount, storeQuery.data?.terminalAppliesSurcharge ?? true),
    [cashDiscount, storeQuery.data?.terminalAppliesSurcharge],
  );
  const cashSavings =
    cashDiscount.discountAmount > 0 && cashDiscount.cashTotal < cashDiscount.cardTotal
      ? Math.round((cashDiscount.cardTotal - cashDiscount.cashTotal) * 100) / 100
      : 0;

  const addUpsellMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => addLineItem(id!, body),
    onSuccess: () => {
      toastSuccess('Added to order');
      void queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function handleUpsellAdd(suggestion: UpsellSuggestionResult) {
    const cfg = suggestion.actionConfig ?? {};
    if (suggestion.actionType === 'add_inventory') {
      const inventoryItemId =
        typeof cfg.inventoryItemId === 'string'
          ? cfg.inventoryItemId
          : typeof cfg.itemId === 'string'
            ? cfg.itemId
            : null;
      const name =
        (typeof cfg.name === 'string' && cfg.name) ||
        suggestion.actionLabel ||
        suggestion.title;
      const price = typeof cfg.price === 'number' ? cfg.price : Number(cfg.price ?? 0) || 0;
      if (inventoryItemId) {
        addUpsellMut.mutate({
          description: name,
          type: 'retail',
          inventoryItemId,
          quantity: 1,
          unitPrice: price,
          discountAmount: 0,
          discountPct: 0,
          taxExempt: false,
        });
        return;
      }
      setUpsellPrefill(name);
      setAddItemOpen(true);
      return;
    }
    if (suggestion.actionType === 'add_service') {
      const name =
        (typeof cfg.serviceName === 'string' && cfg.serviceName) ||
        suggestion.actionLabel ||
        suggestion.title;
      const price = typeof cfg.price === 'number' ? cfg.price : Number(cfg.price ?? 0) || 0;
      addUpsellMut.mutate({
        description: name,
        type: 'labor',
        quantity: 1,
        unitPrice: price,
        discountAmount: 0,
        discountPct: 0,
        taxExempt: true,
      });
    }
  }

  const checkoutMutation = useMutation({
    mutationFn: async (opts?: { managerOverrideAuthorizedBy?: string }) => {
      if (balance.amountToCollect <= 0) {
        return checkout({ ticketId: id!, payments: [] });
      }
      const tip = tipsAllowedForMethod ? tipNum : 0;
      if (tip > 0 && storeQuery.data?.tipsRequirePin !== false && !tipPin.trim()) {
        throw new Error('Enter your staff PIN to authorize the tip');
      }
      const paidAtIso = new Date(paidAtLocal).toISOString();
      const paidAtMeta = {
        paidAt: paidAtIso,
        ...(acknowledgeClosedSession ? { acknowledgeClosedSession: true } : {}),
        ...(opts?.managerOverrideAuthorizedBy
          ? { managerOverrideAuthorizedBy: opts.managerOverrideAuthorizedBy }
          : managerOverrideAuthorizedBy
            ? { managerOverrideAuthorizedBy }
            : {}),
      };
      const lines =
        primaryMethod === 'card'
          ? [
              {
                method: 'card' as const,
                amount: Number(cardDetails.amount || methodDue),
                tipAmount: tip || undefined,
                tipPin: tip > 0 ? tipPin || undefined : undefined,
                transactionId: cardDetails.transactionId.trim() || undefined,
                cardType: cardDetails.cardType || undefined,
                lastFour: cardDetails.lastFour.trim() || undefined,
                ...paidAtMeta,
              },
            ]
          : payments.map((p) => ({
              method: p.method,
              amount: Number(p.amount || methodDue),
              tipAmount: tip || undefined,
              tipPin: tip > 0 ? tipPin || undefined : undefined,
              ...(p.method === 'cash' && cashTendered
                ? { cashTendered: Number(cashTendered) }
                : {}),
              ...paidAtMeta,
            }));
      return checkout({ ticketId: id!, payments: lines });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      await queryClient.invalidateQueries({ queryKey: ['payments', 'ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['cash-session-current'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });

      const closed =
        result &&
        typeof result === 'object' &&
        'ticket' in result &&
        (result as { ticket?: { status?: string; balanceDue?: string } }).ticket?.status === 'closed';

      const remainingAfter = Number(
        (result as { ticket?: { balanceDue?: string } } | undefined)?.ticket?.balanceDue ??
          balance.amountToCollect,
      );

      if (closed || remainingAfter <= 0.009) {
        setSuccess(true);
        toastSuccess(
          balance.amountToCollect <= 0
            ? 'Ticket closed — already paid in full'
            : `Payment of ${formatCurrency(methodDue)} collected`,
        );
      } else {
        setPartialSuccess(true);
        toastSuccess('Partial payment recorded — balance remains');
      }
      if (result.cashSessionWarning) toastError(result.cashSessionWarning);
      else if (result.cashSessionUpdated) toastSuccess('Cash session updated');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      if (parsed.code === 'CLOSED_SESSION_WARNING') {
        setClosedSessionWarning(parsed.message);
        return;
      }
      toastError(parsed.message, parsed.code);
    },
  });

  const signPostWaiverMutation = useMutation({
    mutationFn: (args: {
      signatureDataUrl: string;
      waiverVersion: string;
      collectionMode: WaiverCollectionMode;
    }) =>
      signTicketWaiver(id!, {
        waiverType: 'post_repair',
        signatureDataUrl: args.signatureDataUrl,
        waiverVersion: args.waiverVersion,
        collectionMode: args.collectionMode,
      }),
    onSuccess: () => {
      toastSuccess('Post-repair waiver saved');
      setCheckoutWaiverOpen(false);
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function selectMethod(method: PaymentMethod) {
    setPayments([{ method, amount: '' }]);
    if (method === 'card') {
      const due = amountDueForMethod(cashDiscount, 'card');
      setCardDetails((prev) => ({ ...prev, amount: due }));
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const t = ticketQuery.data?.ticket;
    const needsWaiver = Boolean(storeQuery.data?.postWaiverRequired ?? true) && !t?.postWaiverSignedAt;
    if (needsWaiver && balance.amountToCollect > 0) {
      toastError('Collect the post-repair waiver before completing checkout.');
      return;
    }
    if (balance.isOverpaid) {
      toastError('Customer has a credit balance. Process a refund instead of collecting payment.');
      return;
    }
    if (balance.amountToCollect > 0 && primaryMethod === 'card') {
      const amt = Number(cardDetails.amount);
      if (!(amt > 0) || amt > methodDue + 0.009) {
        toastError('Enter a valid card amount that does not exceed the balance due.');
        return;
      }
    }
    const includesCashPayment =
      balance.amountToCollect > 0 && payments.some((p) => p.method === 'cash');
    if (includesCashPayment && storeQuery.data?.requirePinForCash) {
      const verified = await pinVerification.requestPin();
      if (!verified) return;
    }

    const isBackdate = new Date(paidAtLocal).getTime() < Date.now() - 60_000;
    if (isBackdate && balance.amountToCollect > 0) {
      if (!roleMeetsMinimum(userRole, 'store_manager')) {
        toastError('Backdating a payment requires store manager or higher');
        return;
      }
      if (closedSessionWarning && !acknowledgeClosedSession) {
        toastError('Acknowledge the closed-session warning to continue');
        return;
      }
      const result = await requireOverride({
        action: 'payment.edit',
        title: 'Manager Approval to Backdate Payment',
        description: `Recording payment as of ${paidAtLocal}`,
        context: { reason: 'Backdated checkout payment' },
      });
      if (!result.approved) return;
      checkoutMutation.mutate({ managerOverrideAuthorizedBy: result.authorizedBy.id });
      return;
    }

    checkoutMutation.mutate();
  }

  if (ticketQuery.isLoading) {
    return (
      <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading checkout...</div>
    );
  }

  if (!ticket || !ticketQuery.data) {
    return (
      <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Ticket not found.</div>
    );
  }

  const needsPostWaiver =
    Boolean(storeQuery.data?.postWaiverRequired ?? true) &&
    !ticket.postWaiverSignedAt &&
    balance.amountToCollect > 0;
  const defaultWaiverMode = (storeQuery.data?.waiverCollectionMode ?? 'hand_across') as WaiverCollectionMode;

  if (success) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-green-200 bg-white p-8 text-center shadow-sm">
        <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
        <h2 className="mt-4 text-2xl font-bold text-slate-950">Checkout Complete</h2>
        <p className="mt-2 text-slate-600">Payment was recorded and the ticket was closed.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {id ? (
            <PrintButton type="receipt" ticketId={id} hasPrintNode={hasReceiptPrinter} />
          ) : null}
          <Link
            to={`/tickets/${ticket.id}`}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
          >
            Back to Ticket
          </Link>
        </div>
      </div>
    );
  }

  if (partialSuccess) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-amber-200 bg-white p-8 text-center shadow-sm">
        <h2 className="text-2xl font-bold text-slate-950">Partial payment recorded</h2>
        <p className="mt-2 text-slate-600">
          Remaining balance:{' '}
          <span className="font-semibold text-red-600">{formatCurrency(balance.amountToCollect)}</span>
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
            onClick={() => {
              setPartialSuccess(false);
              setPayments([{ method: 'cash', amount: '' }]);
              setCashTendered('');
            }}
          >
            Collect remaining balance
          </button>
          <Link
            to={`/tickets/${ticket.id}`}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            Back to Ticket
          </Link>
        </div>
      </div>
    );
  }

  const taxMode = storeQuery.data?.taxMode ?? 'parts_only';
  const taxRate = Number(storeQuery.data?.taxRate ?? 0);
  const showPartsOnReceipt = storeQuery.data?.showPartsOnReceipt ?? false;
  const taxLabel =
    taxMode === 'parts_only' && !showPartsOnReceipt
      ? `Tax (parts @ ${(taxRate * 100).toFixed(1)}%)`
      : 'Tax';
  const tendered = Number(cashTendered || 0);
  const cashCollectAmt = Number(payments[0]?.amount || methodDue);
  const tipForChange = tipsAllowedForMethod ? tipNum : 0;
  const changeDue =
    primaryMethod === 'cash' && tendered > 0
      ? Math.max(0, tendered - cashCollectAmt - tipForChange)
      : 0;

  return (
    <form className="grid gap-6 xl:grid-cols-[1fr_380px]" onSubmit={submit}>
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-950">Order Summary</h2>
          <p className="text-sm text-slate-500">{ticket.ticketNumber}</p>
        </div>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Item</th>
              <th className="px-5 py-3">Qty</th>
              <th className="px-5 py-3">Price</th>
              <th className="px-5 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(ticketQuery.data.repairs ?? []).map((repair) => (
              <tr key={repair.id}>
                <td className="px-5 py-4 font-medium text-slate-900">{repair.serviceName}</td>
                <td className="px-5 py-4 text-slate-600">1</td>
                <td className="px-5 py-4 text-slate-600">{formatCurrency(repair.quotedPrice)}</td>
                <td className="px-5 py-4 text-right font-semibold text-slate-950">
                  {formatCurrency(repair.quotedPrice)}
                </td>
              </tr>
            ))}
            {ticketQuery.data.lineItems.map((line) => (
              <tr key={line.id}>
                <td className="px-5 py-4 font-medium text-slate-900">{line.description}</td>
                <td className="px-5 py-4 text-slate-600">{line.quantity}</td>
                <td className="px-5 py-4 text-slate-600">{formatCurrency(line.unitPrice)}</td>
                <td className="px-5 py-4 text-right font-semibold text-slate-950">
                  {formatCurrency(linePreTaxTotal(line))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="space-y-2 border-t border-slate-200 px-5 py-4 text-sm">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatCurrency(ticket.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>{taxLabel}</span>
            <span>{formatCurrency(ticket.taxTotal)}</span>
          </div>
          <div className="flex justify-between border-t border-slate-100 pt-2 font-semibold">
            <span>Total</span>
            <span>{formatCurrency(balance.orderTotal)}</span>
          </div>
        </div>

        <div className="border-t border-slate-200 px-5 py-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
            Payments received
          </h3>
          {activePayments.length === 0 ? (
            <p className="text-sm text-slate-500">No payments recorded yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {activePayments.map((p) => {
                const amt = Number(p.amount);
                const labelParts = [
                  p.isDeposit || (p.notes && /deposit/i.test(p.notes)) ? 'Deposit' : 'Payment',
                  paymentMethodLabel(p.method),
                  formatDate(p.createdAt),
                ];
                return (
                  <li key={p.id} className="flex justify-between py-2">
                    <span className="text-slate-700">{labelParts.filter(Boolean).join(' · ')}</span>
                    <span className={amt < 0 ? 'font-semibold text-amber-700' : 'font-semibold text-green-700'}>
                      {amt < 0 ? '' : '-'}
                      {formatCurrency(Math.abs(amt))}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="mt-3 flex justify-between border-t border-slate-200 pt-3 text-lg font-bold">
            {balance.isOverpaid ? (
              <>
                <span className="text-amber-800">Credit owed</span>
                <span className="text-amber-800">{formatCurrency(balance.creditOwed)}</span>
              </>
            ) : balance.amountToCollect <= 0 ? (
              <>
                <span className="text-green-700">Balance due</span>
                <span className="text-green-700">Paid in full</span>
              </>
            ) : (
              <>
                <span className="text-red-600">Balance due</span>
                <span className="text-red-600">{formatCurrency(balance.amountToCollect)}</span>
              </>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 px-5 py-4">
          {addItemOpen && id ? (
            <CheckoutAddItemPanel
              ticketId={id}
              deviceId={primaryDeviceId}
              initialSearch={upsellPrefill}
              onClose={() => {
                setAddItemOpen(false);
                setUpsellPrefill(undefined);
              }}
              onAdded={() => {
                void ticketQuery.refetch();
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddItemOpen(true)}
              className="rounded-xl border border-dashed border-slate-300 px-4 py-2 text-sm font-semibold text-primary hover:bg-slate-50"
            >
              + Add Item
            </button>
          )}
        </div>
      </section>

      <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-950">
          {balance.amountToCollect <= 0 ? 'Close ticket' : 'Payment'}
        </h2>
        {id && balance.amountToCollect > 0 ? (
          <div className="mt-4">
            <UpsellSuggestionsPanel
              mode="checkout"
              context={{ showAt: 'checkout', ticketId: id }}
              onAddService={handleUpsellAdd}
            />
          </div>
        ) : null}

        {balance.isOverpaid ? (
          <div className="mt-4 space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-semibold">Customer has a credit of {formatCurrency(balance.creditOwed)}.</p>
            <p>Do not collect another payment. Issue a refund from the ticket payments panel.</p>
            <Link
              to={`/tickets/${ticket.id}`}
              className="inline-block rounded-lg bg-amber-800 px-3 py-2 text-xs font-semibold text-white"
            >
              Open ticket to refund
            </Link>
          </div>
        ) : balance.amountToCollect <= 0 ? (
          <div className="mt-4 space-y-4">
            <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-900">
              Paid in full — no further payment needed.
            </p>
            {needsPostWaiver ? null : (
              <button
                type="submit"
                disabled={checkoutMutation.isPending}
                className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-60"
              >
                {checkoutMutation.isPending ? 'Closing…' : 'Close Ticket'}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="mt-4 rounded-xl bg-slate-50 p-4">
              <div className="flex justify-between text-sm">
                <span>Order total</span>
                <span>{formatCurrency(balance.orderTotal)}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm text-green-700">
                <span>Paid to date</span>
                <span>-{formatCurrency(balance.paidTotal)}</span>
              </div>
              <div className="mt-3 flex justify-between border-t border-slate-200 pt-3 text-lg font-bold text-red-600">
                <span>Balance due</span>
                <span>{formatCurrency(balance.amountToCollect)}</span>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Payment date/time
              </p>
              <input
                type="datetime-local"
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                value={paidAtLocal}
                onChange={(e) => {
                  setPaidAtLocal(e.target.value);
                  setClosedSessionWarning(null);
                  setAcknowledgeClosedSession(false);
                }}
              />
              {new Date(paidAtLocal).getTime() < Date.now() - 60_000 ? (
                <p className="mt-1 text-xs text-amber-800">
                  Backdating requires store manager+ and manager PIN on confirm.
                </p>
              ) : null}
              {closedSessionWarning ? (
                <div className="mt-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                  <p className="font-semibold">{closedSessionWarning}</p>
                  <label className="mt-2 flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={acknowledgeClosedSession}
                      onChange={(e) => setAcknowledgeClosedSession(e.target.checked)}
                    />
                    <span>I understand the Z-report for that day will no longer match.</span>
                  </label>
                </div>
              ) : null}
            </div>

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Payment method
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {METHODS.map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => selectMethod(method)}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold capitalize ${
                      primaryMethod === method
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {method === 'store_credit' ? 'Store Credit' : method}
                  </button>
                ))}
              </div>
            </div>

            {cashDiscount.discountAmount > 0 && primaryMethod === 'cash' && cashSavings > 0 ? (
              <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                Customer saves {formatCurrency(cashSavings)} by paying cash on the remaining balance!
              </p>
            ) : null}

            {primaryMethod === 'card' ? (
              <div className="mt-4 space-y-3">
                {terminalAddsSurcharge ? (
                  <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
                    <div className="flex justify-between text-base font-bold text-slate-950">
                      <span>Amount due</span>
                      <span>{formatCurrency(readerAmount)}</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Enter this into the card reader. The reader adds the{' '}
                      {(cashDiscount.discountRate * 100).toFixed(2)}% surcharge.
                    </p>
                    <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold">
                      <span>Amount charged</span>
                      <span>{formatCurrency(chargedAmount)}</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      What the customer pays after the reader adds the fee.
                    </p>
                  </div>
                ) : cashDiscount.discountAmount > 0 ? (
                  <div className="space-y-1 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
                    <div className="flex justify-between">
                      <span>Cash price</span>
                      <span className="font-semibold">{formatCurrency(cashDiscount.cashTotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Card price</span>
                      <span className="font-semibold">{formatCurrency(cashDiscount.cardTotal)}</span>
                    </div>
                    <div className="mt-2 border-t border-slate-200 pt-2">
                      <div className="flex justify-between text-base font-bold text-primary">
                        <span>{terminalEntry.label}</span>
                        <span>{formatCurrency(terminalEntry.amount)}</span>
                      </div>
                      {terminalEntry.detail ? (
                        <p className="mt-1 text-xs text-slate-500">({terminalEntry.detail})</p>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <ManualCardPaymentForm
                  invoiceTotal={terminalAddsSurcharge ? readerAmount : methodDue}
                  maxAmount={methodDue}
                  value={cardDetails}
                  onChange={setCardDetails}
                  {...(terminalAddsSurcharge ? { readerAmount } : {})}
                  {...(terminalAddsSurcharge
                    ? {}
                    : cashDiscount.discountAmount > 0 && cashDiscount.mode === 'card_surcharge'
                      ? {
                          helperText: `Card price / balance due (${(cashDiscount.discountRate * 100).toFixed(2)}% program)`,
                        }
                      : cashDiscount.discountAmount > 0
                        ? { helperText: 'No cash discount for card payments' }
                        : {})}
                />
              </div>
            ) : (
              <div className="mt-4 space-y-3 rounded-xl border border-slate-200 p-4">
                {cashDiscount.discountAmount > 0 && primaryMethod === 'cash' ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span>Balance</span>
                      <span>{formatCurrency(cashDiscount.originalTotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-emerald-700">
                      <span>
                        {cashDiscount.discountLabel} (
                        {(cashDiscount.discountRate * 100).toFixed(2)}%)
                      </span>
                      <span>-{formatCurrency(cashDiscount.discountAmount)}</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
                      <span>Cash total</span>
                      <span>{formatCurrency(methodDue)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm font-semibold">
                    <span>Amount due</span>
                    <span>{formatCurrency(methodDue)}</span>
                  </div>
                )}
                {primaryMethod === 'cash' ? (
                  <>
                    <label className="block text-sm">
                      <span className="font-medium text-slate-700">
                        Amount to collect (leave blank for full balance)
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={payments[0]?.amount ?? ''}
                        placeholder={methodDue.toFixed(2)}
                        onChange={(e) =>
                          setPayments([{ method: 'cash', amount: e.target.value }])
                        }
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-slate-700">Amount tendered</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={cashTendered}
                        onChange={(e) => setCashTendered(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      />
                    </label>
                    <div className="flex justify-between text-sm">
                      <span>Change due</span>
                      <span className="font-semibold">{formatCurrency(changeDue)}</span>
                    </div>
                  </>
                ) : (
                  <label className="block text-sm">
                    <span className="font-medium text-slate-700">Amount (leave blank for full)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={payments[0]?.amount ?? ''}
                      placeholder={methodDue.toFixed(2)}
                      onChange={(e) =>
                        setPayments([{ method: primaryMethod, amount: e.target.value }])
                      }
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </label>
                )}
              </div>
            )}

            {tipsAllowedForMethod ? (
              <div className="mt-4 space-y-2 rounded-xl border border-slate-200 p-4">
                <div className="flex justify-between text-sm">
                  <span>{terminalAddsSurcharge ? 'Amount due' : 'Amount'}</span>
                  <span className="font-semibold">
                    {formatCurrency(terminalAddsSurcharge ? readerAmount : methodDue)}
                  </span>
                </div>
                {terminalAddsSurcharge ? (
                  <div className="flex justify-between text-sm">
                    <span>
                      Card surcharge ({(cashDiscount.discountRate * 100).toFixed(2)}%)
                    </span>
                    <span className="font-semibold">
                      {formatCurrency(cashDiscount.discountAmount)}
                    </span>
                  </div>
                ) : null}
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">
                    {primaryMethod === 'card'
                      ? 'Tip (enter what the terminal reported)'
                      : 'Tip'}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={tipAmount}
                    onChange={(e) => setTipAmount(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    placeholder="0.00"
                  />
                </label>
                {tipNum > 0 && storeQuery.data?.tipsRequirePin !== false ? (
                  <label className="block text-sm">
                    <span className="font-medium text-slate-700">Your PIN (binds tip to you)</span>
                    <input
                      type="password"
                      inputMode="numeric"
                      autoComplete="off"
                      value={tipPin}
                      onChange={(e) => setTipPin(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      placeholder="••••"
                    />
                  </label>
                ) : null}
                <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
                  <span>Total charged</span>
                  <span>{formatCurrency(totalCharged)}</span>
                </div>
                <p className="text-xs text-slate-500">
                  Tip does not change ticket balance due and is not taxable.
                </p>
              </div>
            ) : null}

            {needsPostWaiver ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-semibold">Post-repair waiver required before checkout.</p>
                <button
                  type="button"
                  onClick={() => setCheckoutWaiverOpen(true)}
                  className="mt-2 rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Collect signature
                </button>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={checkoutMutation.isPending || needsPostWaiver}
              className="mt-5 w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-60"
            >
              {checkoutMutation.isPending
                ? 'Processing…'
                : primaryMethod === 'card'
                  ? terminalAddsSurcharge
                    ? `Process card — enter ${formatCurrency(readerAmount)}`
                    : 'Process Card Payment'
                  : `Collect ${formatCurrency(totalCharged)}`}
            </button>
          </>
        )}
      </aside>

      {pinVerification.isOpen ? (
        <PinDialog
          open={pinVerification.isOpen}
          onVerified={pinVerification.handleVerified}
          onCancel={pinVerification.handleCancel}
        />
      ) : null}
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
      {id ? (
        <WaiverModal
          open={checkoutWaiverOpen}
          onClose={() => setCheckoutWaiverOpen(false)}
          ticketId={id}
          waiverType="post_repair"
          defaultCollectionMode={defaultWaiverMode}
          onSigned={async ({ signatureDataUrl, waiverVersion, collectionMode }) => {
            await signPostWaiverMutation.mutateAsync({
              signatureDataUrl,
              waiverVersion,
              collectionMode,
            });
          }}
          onDisplaySignatureComplete={async () => {
            await queryClient.invalidateQueries({ queryKey: ['ticket', id] });
          }}
        />
      ) : null}
    </form>
  );
}
